import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { TrendingUp, Users, Store, Activity } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function DashboardOverview() {
    const [stats, setStats] = useState({ establishments: 0, waiters: 0, calls: 0 });
    const [loading, setLoading] = useState(true);

    // Mock data for the chart since we don't have historical timeseries aggregated in the DB currently
    const data = [
        { name: 'Mon', calls: 400 },
        { name: 'Tue', calls: 300 },
        { name: 'Wed', calls: 550 },
        { name: 'Thu', calls: 278 },
        { name: 'Fri', calls: 1890 },
        { name: 'Sat', calls: 2390 },
        { name: 'Sun', calls: 3490 },
    ];

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            // Run parallel loose count queries
            const [estRes, waiterRes, callsRes] = await Promise.all([
                supabase.from('establishments').select('*', { count: 'exact', head: true }),
                supabase.from('waiters').select('*', { count: 'exact', head: true }),
                supabase.from('waiter_calls').select('*', { count: 'exact', head: true })
            ]);

            setStats({
                establishments: estRes.count || 0,
                waiters: waiterRes.count || 0,
                calls: callsRes.count || 0
            });
        } catch (error) {
            console.error('Error fetching dashboard stats:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div>Loading Analytics...</div>;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                <div>
                    <h1 style={{ fontSize: '1.75rem', fontFamily: 'var(--font-display)' }}>System Overview</h1>
                    <p style={{ color: 'var(--text-secondary)' }}>Real-time platform metrics</p>
                </div>
            </div>

            {/* KPI Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '32px' }}>
                <div className="card metric-card">
                    <div className="metric-icon-wrapper" style={{ background: 'rgba(59, 130, 246, 0.1)' }}>
                        <Store color="var(--accent-primary)" size={24} />
                    </div>
                    <div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', fontWeight: 500 }}>Total Establishments</div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, fontFamily: 'var(--font-display)' }}>{stats.establishments}</div>
                    </div>
                </div>

                <div className="card metric-card">
                    <div className="metric-icon-wrapper" style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
                        <Users color="var(--accent-success)" size={24} />
                    </div>
                    <div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', fontWeight: 500 }}>Active Staff</div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, fontFamily: 'var(--font-display)' }}>{stats.waiters}</div>
                    </div>
                </div>

                <div className="card metric-card">
                    <div className="metric-icon-wrapper" style={{ background: 'rgba(245, 158, 11, 0.1)' }}>
                        <Activity color="var(--accent-warning)" size={24} />
                    </div>
                    <div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', fontWeight: 500 }}>All-Time Calls</div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, fontFamily: 'var(--font-display)' }}>{stats.calls}</div>
                    </div>
                </div>
            </div>

            {/* Chart */}
            <div className="card">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
                    <TrendingUp size={20} color="var(--accent-primary)" />
                    <h3 style={{ fontSize: '1.125rem', fontWeight: 600 }}>Call Volume (Weekly Projection)</h3>
                </div>

                <div style={{ width: '100%', height: '400px' }}>
                    <ResponsiveContainer>
                        <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="colorCalls" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="var(--accent-primary)" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="var(--accent-primary)" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-heavy)" vertical={false} />
                            <XAxis dataKey="name" stroke="var(--text-secondary)" tick={{ fontSize: 12 }} dy={10} axisLine={false} tickLine={false} />
                            <YAxis stroke="var(--text-secondary)" tick={{ fontSize: 12 }} dx={-10} axisLine={false} tickLine={false} />
                            <Tooltip
                                contentStyle={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-light)', borderRadius: '8px' }}
                                itemStyle={{ color: 'var(--text-primary)' }}
                            />
                            <Area type="monotone" dataKey="calls" stroke="var(--accent-primary)" strokeWidth={3} fillOpacity={1} fill="url(#colorCalls)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}
